import { Alert, AlertDescription, AlertIcon, Box, Text } from '@chakra-ui/react'
import { useEffect, useMemo, useState } from 'react'

import { DetailField } from '../../../components/DetailField'
import { FormField } from '../../../components/FormField'
import { SearchableSelect } from '../../../components/Select'
import { formatDate, useAppToast } from '../../../helpers'
import { OrganizationalLevel } from '../../../services/JobPosition/types'
import { EligibleMovementRequest, JobRequisitionDetail } from '../../../services/JobRequisition/types'
import { KnowledgeAreaService } from '../../../services/KnowledgeArea/KnowledgeAreaServices'
import { ORGANIZATIONAL_LEVEL_LABELS } from '../JobPositions/constants'

/** What an RP takes from its MP and its role, shown read-only in the form and in the view. */
export interface IRequisitionSource {
  mpLabel: string
  roleName: string
  sectorName: string | null
  level: OrganizationalLevel | null
  reason: string
  replacedEmployee: string | null
  justificationLabel: string | null
  justification: string | null
  descriptiveOk: boolean | null
  descriptiveChanges: string | null
}

/** "Tempo de Fechamento": "1 dia útil", "12 dias úteis". */
export const businessDaysLabel = (days: number) => (days === 1 ? '1 dia útil' : `${days} dias úteis`)

/** "MP #41 · Aumentar a Equipe · Fulana · 07/10/2026" */
// `created_at` is an instant (UTC): its date part would show the next day late in the evening.
export const mpOptionLabel = (mp: { id: number; type: string; name: string; created_at: string }) =>
  `MP #${mp.id} · ${mp.type} · ${mp.name} · ${formatDate(mp.created_at)}`

export const sourceFromMp = (mp: EligibleMovementRequest): IRequisitionSource => ({
  mpLabel: mpOptionLabel({ id: mp.id, type: mp.type, name: mp.requester_name, created_at: mp.created_at }),
  roleName: mp.job_position?.name ?? '',
  sectorName: mp.job_position?.sector_name ?? null,
  level: mp.job_position?.organizational_level ?? null,
  reason: mp.reason,
  replacedEmployee: mp.replaced_employee,
  justificationLabel: mp.justification_label,
  justification: mp.justification,
  descriptiveOk: mp.descriptive_ok,
  descriptiveChanges: mp.descriptive_changes
})

export const sourceFromDetail = (detail: JobRequisitionDetail): IRequisitionSource => {
  const mp = detail.personnel_movement_request

  return {
    mpLabel: mp
      ? mpOptionLabel({ id: mp.id, type: mp.type, name: mp.name, created_at: mp.created_at })
      : `MP #${detail.personnel_movement_request_id}`,
    roleName: detail.job_position.name,
    sectorName: detail.sector?.name ?? null,
    level: detail.organizational_level,
    reason: detail.reason,
    replacedEmployee: detail.replaced_employee,
    justificationLabel: detail.justification_label,
    justification: detail.justification,
    descriptiveOk: mp?.descriptive_ok ?? null,
    descriptiveChanges: mp?.descriptive_changes ?? null
  }
}

/** Cargo, setor, nível, motivo, substituído e justificativa. */
export function RequisitionSourceFields({ source }: { source: IRequisitionSource }) {
  return (
    <>
      <DetailField label="Cargo da RP" value={source.roleName} />
      <DetailField label="Setor" value={source.sectorName} />
      <DetailField
        label="Nível Organizacional"
        value={source.level ? ORGANIZATIONAL_LEVEL_LABELS[source.level] : null}
      />
      <DetailField label="Motivo da Requisição" value={source.reason} />
      {source.replacedEmployee !== null && (
        <DetailField label="Colaborador a ser substituído" value={source.replacedEmployee} />
      )}
      <DetailField label={source.justificationLabel ?? 'Justificativa da Requisição'} value={source.justification} />
    </>
  )
}

/** What the requester answered on the MP's description page, for RH to adjust before sending. */
export function DescriptiveNotice({ source }: { source: IRequisitionSource }) {
  if (source.descriptiveOk === null) return null

  return source.descriptiveOk ? (
    <Alert status="success" borderRadius="md">
      <AlertIcon />
      <AlertDescription>Descritivo Ok: o gestor seguiu com o descritivo do cargo na MP.</AlertDescription>
    </Alert>
  ) : (
    <Alert status="warning" borderRadius="md" alignItems="flex-start">
      <AlertIcon />
      <Box>
        <AlertDescription display="block" fontWeight="semibold">
          O gestor pediu alterações no descritivo:
        </AlertDescription>
        <Text whiteSpace="pre-wrap" mt={1}>
          {source.descriptiveChanges}
        </Text>
      </Box>
    </Alert>
  )
}

interface IAreasFieldProps {
  value: string[]
  onChange: (value: string[]) => void
  error?: string[]
  isDisabled?: boolean
}

/**
 * Knowledge areas of the vacancy, by name: the name is what the site and the job application
 * validate against, as in the old vacancy registry. A saved name missing from the registry
 * stays selectable, flagged.
 */
export function AreasField({ value, onChange, error, isDisabled }: IAreasFieldProps) {
  const toast = useAppToast()
  const [areas, setAreas] = useState<string[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    new KnowledgeAreaService()
      .list()
      .then((list) => setAreas(list.map((area) => area.name)))
      .catch((err) => toast.error('Erro ao carregar as áreas', err))
      .finally(() => setLoading(false))
  }, [])

  const options = useMemo(() => {
    const missing = value.filter((name) => !areas.includes(name))
    return [
      ...areas.map((name) => ({ value: name, label: name })),
      ...missing.map((name) => ({ value: name, label: `${name} (não cadastrada)` }))
    ].sort((a, b) => a.label.localeCompare(b.label))
  }, [areas, value])

  return (
    <FormField label="Áreas" error={error} helperText="Opcional. O candidato escolhe uma delas no site.">
      <SearchableSelect<string>
        isMulti
        options={options}
        value={value}
        onChange={(names) => onChange([...names].sort((a, b) => a.localeCompare(b)))}
        placeholder="Selecione uma ou mais áreas"
        entity="área"
        feminine
        isLoading={loading}
        isDisabled={isDisabled}
      />
    </FormField>
  )
}
